import React from 'react'
import { Table, Text, Skeleton, ActionIcon, Tooltip, Group, Badge } from '@mantine/core'
import { IconEdit, IconTrash, IconHistory, IconExternalLink, IconShoppingCartPlus, IconShoppingCartOff } from '@tabler/icons-react'
import dayjs from 'dayjs'

const labelTable = [
    { label: 'ID', width: 60 },
    { label: 'SKU', width: 160 },
    { label: 'Product Name', width: 180 },
    { label: 'Description', width: 180 },
    { label: 'Stock Qty', width: 110 },
    { label: 'Category', width: 130 },
    { label: 'Warehouse', width: 130 },
    { label: 'Owner', width: 150 },
    { label: 'Created By', width: 160 },
    { label: 'Updated By', width: 160 },
    { label: 'Action', width: 120 },
]

const UserCell = ({ user, date, onGoToUser }) => {
    if (user && user.id !== null) {
        return (
            <div>
                <Group gap={4} justify="center">
                    <Text size="xs" c="blue" fw={500} lineClamp={2}>{user.name}</Text>
                    <ActionIcon variant="subtle" size="xs" onClick={() => onGoToUser(user.id)}>
                        <IconExternalLink size={12} />
                    </ActionIcon>
                </Group>
                {date && (
                    <Text size="10px" c="dimmed" ta="center">
                        {dayjs(date).format('DD/MMMM/YYYY')} {dayjs(date).format('HH:mm:ss')}
                    </Text>
                )}
            </div>
        )
    }
    return (
        <div>
            <Text size="xs" c="blue" ta="center">Unknown User</Text>
            {date && (
                <Text size="10px" c="dimmed" ta="center">
                    {dayjs(date).format('DD/MMMM/YYYY')} {dayjs(date).format('HH:mm:ss')}
                </Text>
            )}
        </div>
    )
}

const TableInventory = ({ data, loading, onEdit, onDelete, onMovementHistory, onGoToUser, orderItems, onAddToOrder, onRemoveFromOrder }) => {
    if (loading) {
        return (
            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        {labelTable.map((col, i) => (
                            <Table.Th key={i} w={col.width}>{col.label}</Table.Th>
                        ))}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {Array.from({ length: 10 }).map((_, i) => (
                        <Table.Tr key={i}>
                            {labelTable.map((_, j) => (
                                <Table.Td key={j}><Skeleton height={16} radius="sm" /></Table.Td>
                            ))}
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        )
    }

    if (!data || data.length === 0) {
        return (
            <Text ta="center" c="dimmed" py={40}>No inventory data found</Text>
        )
    }

    const isInOrder = (id) => orderItems.some(e => Number(e.id) === Number(id))

    return (
        <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        {labelTable.map((col, i) => (
                            <Table.Th key={i} w={col.width}>{col.label}</Table.Th>
                        ))}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.map((item) => (
                        <Table.Tr key={item.id}>
                            <Table.Td>
                                <Group gap={4}>
                                    <Text size="xs">{item.id}</Text>
                                    <Tooltip label="Edit SKU">
                                        <ActionIcon variant="subtle" size="xs" onClick={() => onEdit(item.id)}>
                                            <IconEdit size={14} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Table.Td>
                            <Table.Td>
                                <Text size="xs">{item.sku}</Text>
                                <Group gap={4} mt={2}>
                                    <Tooltip label="Movement History">
                                        <ActionIcon variant="subtle" size="xs" onClick={() => onMovementHistory(item)}>
                                            <IconHistory size={14} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Delete">
                                        <ActionIcon variant="subtle" size="xs" color="red" onClick={() => onDelete(item)}>
                                            <IconTrash size={14} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Table.Td>
                            <Table.Td>
                                <Text size="xs" fw={700} c="blue" tt="capitalize">{item.productName}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Text size="xs">{item.description}</Text>
                            </Table.Td>
                            <Table.Td ta="center">
                                <Text size="xs">{item.quantity} {item.quantityUnit}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Text size="xs" tt="capitalize">{item.category}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Text size="xs" tt="capitalize">{item.warehouse}</Text>
                            </Table.Td>
                            <Table.Td>
                                <UserCell user={item.owner} onGoToUser={onGoToUser} />
                            </Table.Td>
                            <Table.Td>
                                <UserCell user={item.creator} date={item.createdAt} onGoToUser={onGoToUser} />
                            </Table.Td>
                            <Table.Td>
                                <UserCell user={item.editor} date={item.updatedAt} onGoToUser={onGoToUser} />
                            </Table.Td>
                            <Table.Td ta="center">
                                {isInOrder(item.id) ? (
                                    <Badge
                                        color="green"
                                        variant="light"
                                        style={{ cursor: 'pointer' }}
                                        leftSection={<IconShoppingCartOff size={12} />}
                                        onClick={() => onRemoveFromOrder(item.id)}
                                    >
                                        Remove
                                    </Badge>
                                ) : (
                                    <Badge
                                        color="blue"
                                        variant="light"
                                        style={{ cursor: 'pointer' }}
                                        leftSection={<IconShoppingCartPlus size={12} />}
                                        onClick={() => onAddToOrder(item)}
                                    >
                                        Add
                                    </Badge>
                                )}
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </div>
    )
}

export default TableInventory
